<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { useScheduleStore, GENERIC_TASK_ICON } from '@/stores/schedule.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useNotify } from '@/composables/notify'
import { useClone, useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useDetectDevice } from '@/composables/device'
import { useFilteredOptions } from '@/composables/select-filter'
import { useTaskActions, useTaskServiceOptions } from '@/composables/tasks'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import FormSectionTitle from '@/components/shared/FormSectionTitle.vue'
import ExtraValuesEdit from '@/components/shared/extra-fields/ExtraValuesEdit.vue'
import MissingServicesWarning from '@/components/task/MissingServicesWarning.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  projectSelectable: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update', 'cancel'])
useNavigator(() => emit('cancel'))

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { notifySuccess } = useNotify()
const planningStore = usePlanningStore()
const scheduleStore = useScheduleStore()
const webSiteStore = useWebSiteStore()

const form = ref(null)
const editItem = ref({ services: [], assignees: [], extra_values: {} })
const projectServiceIds = ref(null)
const showDeleteDialog = ref(false)
const { isUpdated, isUpdating, setOriginal } = useIsUpdated(editItem)
const { staffOptions, resourceOptions, ensureServicesAsync } = useTaskServiceOptions()
const { deleteAsync } = useTaskActions()

const isNew = computed(() => !editItem.value.id)
const canEdit = computed(() => isNew.value || editItem.value.can_edit)
const selectedType = computed(() => scheduleStore.getTaskTypeById(editItem.value.task_type))
const typeOptions = computed(() => [
  { id: null, name: i18n.t('common.tasks.generic'), icon: GENERIC_TASK_ICON },
  ...scheduleStore.getTaskTypes().filter(taskType => !taskType.archived || taskType.id === editItem.value.task_type),
])
const missingServiceIds = computed(() => (projectServiceIds.value
  ? editItem.value.services.filter(id => !projectServiceIds.value.includes(id))
  : []))

const { options: filteredStaffOptions, onFilter: onStaffFilter } = useFilteredOptions(() => staffOptions.value)
const { options: filteredServiceOptions, onFilter: onServiceFilter } = useFilteredOptions(() => resourceOptions.value)
const { options: filteredProjectOptions, onFilter: onProjectFilter } = useFilteredOptions(() => planningStore.projects
  .filter(project => project.id === editItem.value.project || (!project.archived && project.status < planningStore.projectStatusEnum.COMPLETED)))

const loadProjectServiceIdsAsync = async() => {
  projectServiceIds.value = editItem.value.project ? await scheduleStore.fetchProjectServiceIdsAsync(editItem.value.project) : null
}

onMounted(async() => {
  editItem.value = {
    ...useClone(props.modelValue),
    services: [...(props.modelValue.services ?? [])],
    assignees: [...(props.modelValue.assignees ?? [])],
    extra_values: { ...props.modelValue.extra_values },
  }
  setOriginal(editItem.value)
  await Promise.all([
    scheduleStore.ensureTaskTypesAsync(),
    ensureServicesAsync(),
    loadProjectServiceIdsAsync(),
    props.projectSelectable && !planningStore.projects.length ? planningStore.fetchProjectsAsync({ all: true }, true) : null,
  ])
})

const onProjectChange = async(projectId) => {
  editItem.value.project = projectId
  await loadProjectServiceIdsAsync()
}

const onSave = async() => {
  const valid = await form.value.validate()
  if (!valid) return
  isUpdating.value = true
  try {
    const { data } = await scheduleStore.upsertTaskAsync({
      ...editItem.value,
      due_date: editItem.value.due_date || null,
      due_time: editItem.value.due_time || null,
    })
    notifySuccess(i18n.t(isNew.value ? 'info.createdSuccessfully' : 'info.updatedSuccessfully'))
    emit('update', data)
  } finally {
    isUpdating.value = false
  }
}

const onDelete = async() => {
  await deleteAsync(editItem.value.id)
  emit('update')
}
</script>

<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDelete"
    />
    <navigate-back
      :text="isNew ? i18n.t('common.tasks.newTask') : editItem.title"
      @cancel="emit('cancel')"
    />
    <q-card-section class="scroll col q-pa-md" :class="{ 'dialog-card-content': !isNew }">
      <q-form ref="form">
        <div class="row q-col-gutter-sm">
          <q-input
            v-model="editItem.title"
            :class="isMobile ? 'col-12' : 'col-8'"
            :label="i18n.t('common.title')"
            :readonly="!canEdit"
            autofocus
            :rules="[val => !!val || i18n.t('common.required')]"
          />
          <q-select
            v-model="editItem.task_type"
            :class="isMobile ? 'col-12' : 'col-4'"
            :label="i18n.t('common.type')"
            :options="typeOptions"
            option-value="id"
            option-label="name"
            emit-value
            map-options
            :readonly="!canEdit"
          >
            <template #prepend>
              <q-icon :name="selectedType?.icon ?? GENERIC_TASK_ICON" :style="selectedType ? { color: selectedType.color } : undefined" />
            </template>
            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section avatar>
                  <q-icon :name="scope.opt.icon" :style="scope.opt.color ? { color: scope.opt.color } : undefined" />
                </q-item-section>
                <q-item-section>{{ scope.opt.name }}</q-item-section>
              </q-item>
            </template>
          </q-select>
          <q-select
            v-if="projectSelectable"
            :model-value="editItem.project"
            class="col-12"
            :label="i18n.t('common.project')"
            :hint="editItem.project ? undefined : i18n.t('common.tasks.withoutProjectHint')"
            :options="filteredProjectOptions"
            option-value="id"
            option-label="name"
            emit-value
            map-options
            clearable
            use-input
            :input-debounce="webSiteStore.getInputDebounce()"
            :readonly="!canEdit"
            @filter="onProjectFilter"
            @update:model-value="onProjectChange"
          />
          <q-input
            v-model="editItem.description"
            class="col-12"
            :label="i18n.t('common.description')"
            :readonly="!canEdit"
            autogrow
          />
          <form-section-title :label="i18n.t('common.tasks.planning')" />
          <q-input
            v-model="editItem.due_date"
            class="col-6"
            :label="i18n.t('common.date')"
            type="date"
            stack-label
            :readonly="!canEdit"
          />
          <q-input
            v-model="editItem.due_time"
            class="col-6"
            :label="i18n.t('common.time')"
            type="time"
            stack-label
            :readonly="!canEdit"
            :disable="!editItem.due_date"
          />
          <q-select
            v-model="editItem.assignees"
            class="col-12"
            :label="i18n.t('common.tasks.assignee')"
            :options="filteredStaffOptions"
            option-value="id"
            option-label="name"
            emit-value
            map-options
            multiple
            use-chips
            use-input
            :input-debounce="webSiteStore.getInputDebounce()"
            :readonly="!canEdit"
            @filter="onStaffFilter"
          />
          <q-select
            v-model="editItem.services"
            class="col-12"
            :label="i18n.t('common.tasks.resources')"
            :options="filteredServiceOptions"
            option-value="id"
            option-label="name"
            emit-value
            map-options
            multiple
            use-chips
            use-input
            :input-debounce="webSiteStore.getInputDebounce()"
            :readonly="!canEdit"
            @filter="onServiceFilter"
          />
          <missing-services-warning class="col-12" :service-ids="missingServiceIds" />
          <template v-if="selectedType?.extra_fields.length">
            <form-section-title :label="selectedType.name" />
            <extra-values-edit
              v-model="editItem.extra_values"
              :fields="selectedType.extra_fields"
              :compact="isMobile"
              :readonly="!canEdit"
            />
          </template>
        </div>
      </q-form>
    </q-card-section>
    <q-card-actions v-if="isNew" align="right" class="q-pa-md">
      <q-btn
        color="primary"
        :label="i18n.t('common.add')"
        :loading="isUpdating"
        @click="onSave"
      />
    </q-card-actions>
    <q-card-actions v-else-if="canEdit" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="showDeleteDialog = true"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        @click="onSave"
      />
    </q-card-actions>
  </q-card>
</template>
